import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { toast } from "react-toastify";
import { orange500, zeeguuOrange } from "../components/colors";
import { APIContext } from "../contexts/APIContext";
import { UserContext } from "../contexts/UserContext";
import LoadingAnimation from "../components/LoadingAnimation";
import useListeningSession from "../hooks/useListeningSession";
import useDailyLessonPreference from "../hooks/useDailyLessonPreference";
import { useIsOffline } from "../contexts/ConnectivityContext";
import { AUDIO_STATUS, GENERATION_PROGRESS } from "./AudioLessonConstants";
import TodayEpisodeCard from "./TodayEpisodeCard";
import DailyLessonSettingsDialog from "./DailyLessonSettingsDialog";
import DailyLessonSettingsPill from "./DailyLessonSettingsPill";
import { SubtleTextButton } from "./LessonView.sc";
import { BannerButton } from "./SharedLessonView.sc";
import { wordsAsTile, shortDate } from "./audioUtils";

// Shown rotating during the backend phases that don't emit sub-step
// progress (no record yet, "pending", "generating_script",
// "combining_audio"). Only "synthesizing_audio" has a moving
// step counter, so during that phase we surface the backend message
// verbatim instead.
const PLACEHOLDER_PROGRESS_MESSAGES = [
  "Warming up...",
  "Picking content for today's lesson...",
  "Drafting the dialogue...",
  "Picking interesting vocabulary...",
  "Crafting natural conversation...",
  "Adapting for your level...",
];
const PLACEHOLDER_ROTATION_MS = 2500;

export default function TodayAudio() {
  const api = useContext(APIContext);
  const { userDetails, setUserDetails } = useContext(UserContext);
  const lang = userDetails?.learned_language || "";

  const { dailyType, dailySuggestion, prefLoaded, isConfigured, saveDailyLesson } = useDailyLessonPreference(api, lang);

  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(null);
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  // What the in-progress generation is about, so the progress screen can name
  // it. { type: backendLessonType, suggestion }
  const [generatingLabel, setGeneratingLabel] = useState(null);
  const [lessonData, setLessonData] = useState(null);
  const [noLesson, setNoLesson] = useState(false); // confirmed: nothing for today yet
  const [error, setError] = useState(null);
  // Couldn't reach the backend at all (offline / slow network) while loading
  // today's state. Kept separate from `noLesson` so we don't mistake a network
  // failure for "you haven't set up daily lessons yet" and show the setup screen.
  const [connectionError, setConnectionError] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [currentPlaybackTime, setCurrentPlaybackTime] = useState(0);
  const history = useHistory();

  // Daily-subscription state for this language (status / awaiting-engagement /
  // next-lesson date), carried on the today's-lesson response. Drives the status
  // box and the empty/off states. The backend subscription + cron are the source
  // of truth for whether a lesson should exist today — the client never
  // auto-generates; the only client-initiated generation is first-ever setup via
  // the settings dialog.
  const [subscription, setSubscription] = useState(null);

  // Capture dailyType/dailySuggestion in refs so the polling effect can read
  // them without needing them in its deps. Putting them in deps re-runs the
  // effect mid-generation; its cleanup `stopPolling` sets isGenerating=false,
  // which in turn makes the next effect run return early — silently killing
  // an in-flight adopted generation when dailyType resolves a moment after.
  const dailyTypeRef = useRef(dailyType);
  const dailySuggestionRef = useRef(dailySuggestion);
  dailyTypeRef.current = dailyType;
  dailySuggestionRef.current = dailySuggestion;

  // Listening session tracking via hook
  const listeningSession = useListeningSession(lessonData?.lesson_id);
  const words = lessonData?.words || [];

  // Re-initialize when the learned language changes
  useEffect(() => {
    setLessonData(null);
    setSubscription(null);
    setError(null);
    setNoLesson(false);
    setConnectionError(false);
    // Tear down any in-flight generation/polling tied to the previous language
    // so we don't keep polling/showing progress for the wrong language.
    setIsGenerating(false);
    setGenerationProgress(null);
  }, [lang]);

  // Kick off generation for a given (backend lesson type, raw subject). Used by
  // the auto-generate fallback and by the settings dialog (first day / change
  // topic). Reuses the existing background-generation + streaming-progress flow.
  function startGeneration(backendType, rawSuggestion) {
    const isVocab = backendType === "three_words_lesson";
    const trimmedSuggestion = isVocab ? null : (rawSuggestion || "").trim() || null;
    const apiLessonType = isVocab ? null : backendType;

    setGeneratingLabel({ type: backendType, suggestion: trimmedSuggestion || "" });
    setIsGenerating(true);
    setNoLesson(false);
    setError(null);
    setGenerationProgress(null);
    setUserDetails((prev) => ({ ...prev, daily_audio_status: AUDIO_STATUS.GENERATING }));

    api.generateDailyLesson(
      (data) => {
        if (data.status === AUDIO_STATUS.GENERATING) {
          // Generation started in background — polling will deliver the lesson
          return;
        }
        // Existing lesson returned directly
        setIsGenerating(false);
        setGenerationProgress(null);
        setLessonData(data);
        setUserDetails((prev) => ({
          ...prev,
          daily_audio_status: data.is_completed ? AUDIO_STATUS.COMPLETED : AUDIO_STATUS.READY,
        }));
      },
      (err) => {
        // Generation already running — let polling continue
        if (err.message && err.message.toLowerCase().includes("already being generated")) {
          return;
        }
        setIsGenerating(false);
        setGenerationProgress(null);
        setUserDetails((prev) => ({ ...prev, daily_audio_status: null }));

        // "Not enough words" for a vocabulary lesson is actionable — steer to a
        // Topic/Situation (restores the old proactive feasibility guidance).
        let msg;
        if (err.message && err.message.toLowerCase().includes("not enough words")) {
          msg = "Not enough words for a vocabulary lesson yet. Try a Topic or Situation instead.";
        } else {
          msg = err.message || "Couldn't prepare today's lesson. Please try again.";
        }
        setError(msg);
      },
      trimmedSuggestion,
      apiLessonType,
    );
  }

  // Saving from the settings dialog. regenerate=true applies the change to
  // today's lesson now (delete + regenerate); regenerate=false only stores the
  // preference for tomorrow's lesson.
  function handleConfigured(backendType, suggestion, regenerate) {
    saveDailyLesson(backendType, suggestion);
    setSettingsOpen(false);
    if (!regenerate) return;

    setError(null);
    if (lessonData) {
      api.deleteTodaysLesson(
        () => {
          setLessonData(null);
          startGeneration(backendType, suggestion);
        },
        // If the delete fails the backend still has today's lesson, so
        // regenerating would silently hand back the old one — surface an error
        // rather than pretend the change applied.
        (err) => setError(err.rateLimited
          ? err.message
          : "Couldn't refresh today's lesson. Please try again."),
        lessonData.lesson_id,
      );
    } else {
      startGeneration(backendType, suggestion);
    }
  }

  // Refresh today's lesson + subscription state from the backend.
  function refreshToday() {
    api.getTodaysLesson(
      lang,
      (data) => {
        setSubscription(data || null);
        if (data && data.lesson_id) {
          setLessonData(data);
          setNoLesson(false);
        } else {
          setLessonData(null);
          setNoLesson(true);
        }
      },
      () => {},
    );
  }

  function turnOnDailyLessons() {
    api.setDailySubscriptionEnabled(
      lang,
      true,
      () => {
        toast.success("Daily lessons turned on");
        refreshToday();
      },
      () => toast.error("Couldn't turn daily lessons back on. Please try again."),
    );
  }

  function turnOffDailyLessons() {
    api.setDailySubscriptionEnabled(
      lang,
      false,
      () => {
        toast.success("Daily lessons turned off");
        refreshToday();
      },
      () => toast.error("Couldn't turn daily lessons off. Please try again."),
    );
  }

  // Poll for progress while a generation is in flight (started here, or a cron
  // run we adopted on mount). No localStorage flag — `isGenerating` is the only
  // trigger now that the backend owns "should there be a lesson today".
  useEffect(() => {
    if (!isGenerating) {
      return;
    }

    let pollInterval;

    const stopPolling = () => {
      clearInterval(pollInterval);
      setIsGenerating(false);
      setGenerationProgress(null);
    };

    let lessonRetryCount = 0;
    const MAX_LESSON_RETRIES = 3;
    let noProgressCount = 0;
    const MAX_NO_PROGRESS_RETRIES = 5;

    const handleLessonReady = (data) => {
      if (data && data.lesson_id) {
        stopPolling();
        setLessonData(data);
        setSubscription(data);
        setUserDetails((prev) => ({
          ...prev,
          daily_audio_status: data.is_completed ? AUDIO_STATUS.COMPLETED : AUDIO_STATUS.READY,
        }));
        lessonRetryCount = 0;
      } else if (data && data.error) {
        // Lesson exists but has an error (e.g., audio file not ready yet)
        lessonRetryCount++;
        if (lessonRetryCount >= MAX_LESSON_RETRIES) {
          handleError(data.error);
        }
      }
    };

    const handleError = (message) => {
      stopPolling();
      setError(message);
    };

    // Polling found neither a progress record nor a lesson — generation isn't
    // actually running. Stop and show a recoverable error instead of spinning.
    const handleExhausted = () => {
      stopPolling();
      setNoLesson(true);
      setError("We couldn't prepare today's lesson. Try again or pick a different topic.");
    };

    const checkForLesson = () => {
      api.getTodaysLesson(lang, handleLessonReady, () => {});
    };

    const pollForProgress = () => {
      api.getAudioLessonGenerationProgress(
        (progress) => {
          // If the in-flight generation is for a different language than the one
          // we're showing, stop polling — we're watching the wrong thing.
          if (progress && progress.language_code && progress.language_code !== lang) {
            stopPolling();
            return;
          }
          if (progress) {
            noProgressCount = 0;
            setGenerationProgress(progress);
            // Label the screen from the saved subscription type/subject when we
            // didn't start this generation ourselves (adopted a cron run). The
            // cron always generates the subscribed type, so this matches. Read
            // via refs so dailyType resolving later doesn't restart this effect.
            const dt = dailyTypeRef.current;
            if (dt) {
              setGeneratingLabel((prev) => prev || { type: dt, suggestion: dailySuggestionRef.current || "" });
            }

            if (progress.status === GENERATION_PROGRESS.DONE) {
              checkForLesson();
            } else if (progress.status === GENERATION_PROGRESS.ERROR) {
              handleError(progress.message || "Lesson generation failed. Please try again.");
            }
          } else {
            // No progress record - might be a brief gap. Retry a few times.
            noProgressCount++;
            if (noProgressCount <= MAX_NO_PROGRESS_RETRIES) {
              return;
            }
            // Exhausted retries — check if a lesson appeared, otherwise stop
            api.getTodaysLesson(
              lang,
              (data) => {
                if (data && data.lesson_id) {
                  handleLessonReady(data);
                } else {
                  handleExhausted();
                }
              },
              handleExhausted,
            );
          }
        },
        // On progress API error, fall back to checking lesson directly
        checkForLesson,
      );
    };

    pollInterval = setInterval(pollForProgress, 1500);

    // Browsers throttle setInterval for background tabs, so check
    // immediately when the user returns to the app
    const onVisibilityChange = () => {
      if (!document.hidden) {
        pollForProgress();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    // Capacitor doesn't fire visibilitychange reliably on resume — also
    // listen for the native lifecycle event so the lesson updates the
    // moment the user returns to the app.
    let appStateListenerHandle = null;
    let appStateListenerCancelled = false;
    if (Capacitor.getPlatform() !== "web" && Capacitor.isPluginAvailable("App")) {
      (async () => {
        try {
          const { App } = await import("@capacitor/app");
          const handle = await App.addListener("appStateChange", ({ isActive }) => {
            if (isActive) pollForProgress();
          });
          if (appStateListenerCancelled) handle.remove();
          else appStateListenerHandle = handle;
        } catch {
          // Best-effort — fall back to the interval timer
        }
      })();
    }

    return () => {
      stopPolling();
      document.removeEventListener("visibilitychange", onVisibilityChange);
      appStateListenerCancelled = true;
      if (appStateListenerHandle) appStateListenerHandle.remove();
    };
  }, [api, isGenerating]);

  // Rotate placeholder messages only during the early/long phases where the
  // backend message sits static with no sub-step progress.
  const showRealMessage =
    generationProgress?.status === GENERATION_PROGRESS.SYNTHESIZING_AUDIO ||
    generationProgress?.status === GENERATION_PROGRESS.COMBINING_AUDIO ||
    generationProgress?.status === GENERATION_PROGRESS.DONE;
  useEffect(() => {
    if (!isGenerating || showRealMessage) {
      setPlaceholderIndex(0);
      return;
    }
    const id = setInterval(() => {
      setPlaceholderIndex((i) => i + 1);
    }, PLACEHOLDER_ROTATION_MS);
    return () => clearInterval(id);
  }, [isGenerating, showRealMessage]);

  // Update page title and playback time when lessonData changes
  useEffect(() => {
    if (lessonData && lessonData.words) {
      document.title = shortDate() + " Daily Audio: " + wordsAsTile(words);
      const initialTime =
        lessonData.pause_position_seconds || lessonData.position_seconds || lessonData.progress_seconds || 0;
      setCurrentPlaybackTime(initialTime);
    } else {
      document.title = "Zeeguu: Audio Lesson";
    }
  }, [lessonData]);

  // Load today's state: is something generating? else is there a lesson? else
  // nothing yet. A backend that can't be reached (offline / slow network) lands
  // in onLessonError, which flags a connection error so we keep the loading
  // screen (it becomes the offline wait-game) up instead of dropping the
  // subscribed user onto the first-run setup screen.
  const loadToday = useCallback(() => {
    setIsLoading(true);
    // Don't clear connectionError here: on a retry it stays true (no message
    // blink, and the retry effect isn't torn down + rebuilt each attempt) until
    // a load actually succeeds, which clears it in onLesson / the adopt branch.

    const onLesson = (data) => {
      setIsLoading(false);
      setConnectionError(false);
      setSubscription(data || null);
      if (data && data.lesson_id) {
        setLessonData(data);
        setNoLesson(false);
      } else {
        setLessonData(null);
        setNoLesson(true);
      }
    };
    const onLessonError = () => {
      // Couldn't reach the backend. Keep the loading screen (which morphs into
      // the wait-game) up — leaving isLoading true — and let the retry effect
      // re-load once connectivity returns, rather than show the setup screen.
      setConnectionError(true);
    };

    api.getAudioLessonGenerationProgress(
      (progress) => {
        const inFlight = progress && ![GENERATION_PROGRESS.DONE, GENERATION_PROGRESS.ERROR].includes(progress.status);
        // Only adopt when the in-flight progress is for the language we're showing
        // (a Dutch generation shouldn't be surfaced inside the Danish view).
        const matchesLang = inFlight && (!progress.language_code || progress.language_code === lang);
        if (matchesLang) {
          setIsLoading(false);
          setConnectionError(false);
          setIsGenerating(true);
          setGenerationProgress(progress);
          // Label it from the saved subscription (the cron always generates the
          // subscribed type). Read via refs so a late pref resolution doesn't
          // need to be a dependency of this callback.
          if (dailyTypeRef.current) {
            setGeneratingLabel({ type: dailyTypeRef.current, suggestion: dailySuggestionRef.current || "" });
          }
          setUserDetails((prev) => ({ ...prev, daily_audio_status: AUDIO_STATUS.GENERATING }));
          return;
        }
        api.getTodaysLesson(lang, onLesson, onLessonError);
      },
      () => api.getTodaysLesson(lang, onLesson, onLessonError),
    );
  }, [api, lang, setUserDetails]);

  // On mount (and whenever api/lang changes) do the initial load.
  useEffect(() => {
    loadToday();
  }, [loadToday]);

  // Connectivity detection (web events + native OS change + a probe poll +
  // tab/app resume) lives in useIsOffline — no listeners reimplemented here.
  // When it flips back online while today's load is still failed, retry it.
  const isOffline = useIsOffline();
  const wasOffline = useRef(isOffline);
  useEffect(() => {
    const cameOnline = wasOffline.current && !isOffline;
    wasOffline.current = isOffline;
    if (cameOnline && connectionError) loadToday();
  }, [isOffline, connectionError, loadToday]);

  // Seed the dialog with the saved preference; if none is stored yet (e.g. a
  // lesson generated before preferences existed), fall back to today's lesson
  // so the learner always sees their current type/subject pre-selected.
  const settingsDialog = settingsOpen && (
    <DailyLessonSettingsDialog
      api={api}
      initialType={dailyType || lessonData?.lesson_type || null}
      initialSuggestion={(dailyType ? dailySuggestion : lessonData?.canonical_suggestion) || ""}
      alreadySubscribed={
        isConfigured || subscription?.subscription_status === "active" || subscription?.subscription_status === "off"
      }
      onSubmit={handleConfigured}
      onTurnOff={
        subscription?.subscription_status === "active"
          ? () => {
              turnOffDailyLessons();
              setSettingsOpen(false);
            }
          : null
      }
      onDismiss={() => setSettingsOpen(false)}
    />
  );

  // connectionError keeps us here even if isLoading toggled off: the
  // LoadingAnimation diagnoses the offline/slow state and turns into the
  // wait-game, and the retry effect re-loads once we're back online.
  if (isLoading || !prefLoaded || connectionError) {
    return (
      <div style={{ padding: "20px" }}>
        <LoadingAnimation>
          <p>Preparing your daily lesson...</p>
        </LoadingAnimation>
      </div>
    );
  }

  if (isGenerating) {
    let progressDetail = PLACEHOLDER_PROGRESS_MESSAGES[placeholderIndex % PLACEHOLDER_PROGRESS_MESSAGES.length];
    let progressPercent = 1;

    if (generationProgress?.total_segments > 0) {
      const segmentsCompleted = Math.max(0, generationProgress.current_segment - 1);
      let stepsInCurrentSegment = 0;
      if (generationProgress.total_steps > 0) {
        stepsInCurrentSegment = generationProgress.current_step / generationProgress.total_steps;
      }
      progressPercent = Math.max(
        1,
        ((segmentsCompleted + stepsInCurrentSegment) / generationProgress.total_segments) * 100,
      );
    }

    if (showRealMessage) {
      progressDetail = generationProgress.message || progressDetail;
    }

    const label = generatingLabel || {};
    let subtitle;
    let bigTitle;
    if (label.type === "topic") {
      subtitle = "Preparing today's lesson on";
      bigTitle = label.suggestion;
    } else if (label.type === "situation") {
      subtitle = "Preparing today's lesson for";
      bigTitle = label.suggestion;
    } else {
      subtitle = "Preparing today's lesson with";
      bigTitle = "Three of Your Study Words";
    }

    return (
      <div
        style={{
          padding: "20px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          // dvh + explicit safe-area insets (see empty-state note below) so the
          // generating screen fills the space between top bar and bottom nav
          // without spilling into a scrollbar on notch / Dynamic Island phones.
          minHeight: "calc(100dvh - 3rem - 5.5rem - env(safe-area-inset-top) - env(safe-area-inset-bottom))",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            marginTop: "8%",
            maxWidth: "320px",
            width: "100%",
          }}
        >
          <p style={{ fontSize: "14px", color: "var(--text-secondary)", margin: 0, marginBottom: "4px" }}>{subtitle}</p>
          <h1 style={{ color: zeeguuOrange, margin: 0, marginBottom: "20px", fontSize: "2rem" }}>{bigTitle}</h1>

          <div
            style={{
              width: "200px",
              height: "8px",
              backgroundColor: "var(--border-light)",
              borderRadius: "4px",
              overflow: "hidden",
              marginBottom: "10px",
              marginTop: "10px",
            }}
          >
            <div
              style={{
                width: `${Math.min(progressPercent, 100)}%`,
                height: "100%",
                backgroundColor: orange500,
                borderRadius: "4px",
                transition: "width 1s ease-in-out",
              }}
            />
          </div>
          <p
            style={{
              fontSize: "14px",
              color: "var(--text-secondary)",
              marginTop: "8px",
              marginBottom: "16px",
              minHeight: "1.4em",
              transition: "opacity 0.3s ease-in-out",
            }}
          >
            {progressDetail}
          </p>
        </div>

        <div style={{ flex: 1 }} />

        <div
          style={{
            backgroundColor: "var(--bg-secondary)",
            border: "1px solid var(--border-light)",
            borderRadius: "12px",
            padding: "16px 20px",
            maxWidth: "320px",
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          <p style={{ color: "var(--text-primary)", margin: 0, fontSize: "16px", textAlign: "center" }}>
            This can take a moment.
            <br />
            Feel free to browse — your lesson will be here when it's ready.
          </p>
        </div>

        <div style={{ flex: 1 }} />
      </div>
    );
  }

  if (lessonData) {
    return (
      <>
        <TodayEpisodeCard
          lessonData={lessonData}
          setLessonData={setLessonData}
          error={error}
          api={api}
          userDetails={userDetails}
          setUserDetails={setUserDetails}
          listeningSession={listeningSession}
          currentPlaybackTime={currentPlaybackTime}
          setCurrentPlaybackTime={setCurrentPlaybackTime}
          onChangeTopic={() => setSettingsOpen(true)}
        />
        {settingsDialog}
      </>
    );
  }

  // No lesson for today. Render the right subscription state in the shared
  // centered layout: a recoverable error, turned-off, waiting-for-next-day,
  // held-until-you-finish-the-previous, or first-run setup.
  const subStatus = subscription?.subscription_status;

  let heading;
  let body;
  let primaryLabel;
  let primaryAction;
  // Only the "active" state's button generates directly; the others already
  // open the settings dialog (or turn lessons back on), so they need no pill.
  let showSettingsPill = false;

  if (error) {
    heading = "Let's try a different topic";
    body = error;
    primaryLabel = "Change daily topic";
    primaryAction = () => setSettingsOpen(true);
  } else if (subStatus === "off") {
    heading = "Daily lessons are off";
    body = "Turn them back on and a fresh lesson will be waiting on your next day.";
    primaryLabel = "Turn on daily lessons";
    primaryAction = turnOnDailyLessons;
  } else if (subStatus === "active") {
    // A waiting (engagement-paused) lesson arrives as lessonData with paused=true
    // and is handled by the episode card, so this no-lesson path means "subscribed
    // but nothing for today yet" — a cron miss / first day / timezone gap. The
    // cron won't necessarily run again today, so offer to make it now rather than
    // promise a date.
    heading = "No lesson yet today";
    body = "Make today's lesson now, or it'll be ready on your next day.";
    primaryLabel = "Generate today's lesson";
    primaryAction = () => startGeneration(dailyType, dailySuggestion);
    showSettingsPill = true;
  } else {
    // not subscribed — first-run setup
    heading = "A new lesson, daily";
    body = "Choose what you'd like to listen to. We'll make you a fresh lesson on it daily — starting with today's.";
    primaryLabel = "Set up my daily lessons";
    primaryAction = () => setSettingsOpen(true);
  }

  return (
    <>
      <div
        style={{
          padding: "20px",
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          // dvh (not vh) + explicit safe-area insets so this centered empty/setup
          // state fills exactly the space between the top bar and bottom nav
          // without overflowing into a scrollbar on notch / Dynamic Island phones
          // (plain 100vh overcounts there). Same chrome offsets as the lesson view.
          minHeight: "calc(100dvh - 3rem - 5.5rem - env(safe-area-inset-top) - env(safe-area-inset-bottom))",
        }}
      >
        <div style={{ fontSize: "2.5rem" }} aria-hidden>
          🎧
        </div>
        <h2 style={{ color: zeeguuOrange, margin: "8px 0" }}>{heading}</h2>
        <p style={{ color: "var(--text-secondary)", maxWidth: "300px", marginBottom: "24px" }}>{body}</p>
        <BannerButton onClick={primaryAction} style={{ padding: "12px 24px", fontSize: "1rem" }}>
          {primaryLabel}
        </BannerButton>
        {showSettingsPill && (
          <DailyLessonSettingsPill onClick={() => setSettingsOpen(true)} style={{ marginTop: "16px" }} />
        )}
      </div>
      {settingsDialog}
    </>
  );
}
